import PropTypes from 'prop-types';

import { CONSTANTS } from './gtm-key-constants';
import { GTM_KEY_GETTERS } from './gtm-key-getters';
/**
 * @typedef {Object} GtmTagStructure
 * @property {GaEventData.GTM_KEYS|string}  gtmTagStructure[key] - value of any string or GTM_KEY_GETTERS key... eg. DEVICE (gets device)
 *
 * @typedef {Object} GaEventData
 * @property {Function} gaEventData.condition e.g customer=>boolean defaults to TRUE
 * @property {object} gaEventData.CONSTANTS - default constants value
 * @property {object} gaEventData.GTM_KEY_GETTERS - default key getters from data - e.g data=>data.id
 * @property {GtmTagStructure} gaEventData.struct - ga event structure
 *
 *
 *
 * gtmUtils.GTM_KEY_GETTERS for default values of Getters
 * gtmUtils.CONSTANTS for default values of constants
 *
 * Provide CONSTANTS or GTM_KEY_GETTERS to override
 * {
 *  CONSTANTS:{
 *    NEW_CONSTANT:'new_constant'
 *  },
 *  GTM_KEY_GETTERS:{
 *    NEW_GTM_KEY:(data)=>value
 *  }
 * }
 *
 */

const evalKeyValues = (obj, data) =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      return {
        ...acc,
        [key]: value.map(arrayValue => evalKeyValues(arrayValue, data)),
      };
    }
    if (typeof value === 'object' && value !== null) {
      return {
        ...acc,
        [key]: evalKeyValues(value, data),
      };
    }
    const valueCalculated =
      data.GTM_KEY_GETTERS[value] && typeof value !== 'number'
        ? data.GTM_KEY_GETTERS[value](data)
        : value;
    if (typeof valueCalculated === undefined) return acc;
    return {
      ...acc,
      [key]: valueCalculated,
    };
  }, {});

const defaultToTrueFns = (a, data) =>
  a.reduce((acc, fn) => {
    const isFn = typeof fn === 'function';
    const isUndefined = typeof fn === 'undefined';
    return acc && ((!isFn && isUndefined) || (isFn && !!fn(data)));
  }, true);

/**
 * @param {Object} props
 * @param {GaEventData} props.tagConfig - declarative config for gtag build
 * @param {Object} props.data - e.g. raw customer data
 * @param {Function} props.defaultCondition - data=>boolean
 * @returns
 */
export const buildGtag = ({ tagConfig = {}, data = {}, defaultCondition = undefined }) => {
  if (!Object.keys(tagConfig || {}).length || !tagConfig?.struct) return;
  const completeData = {
    CONSTANTS,
    GTM_KEY_GETTERS,
    ...data,
  };
  if (defaultToTrueFns([tagConfig?.condition, defaultCondition], data)) {
    return evalKeyValues(tagConfig.struct, completeData);
  }

  return;
};

buildGtag.propTypes = {
  tagConfig: PropTypes.shape({
    CONSTANTS: PropTypes.object,
    GTM_KEY_GETTERS: PropTypes.object,
    condition: PropTypes.func,
    struct: PropTypes.object,
  }),
  data: PropTypes.object,
  defaultCondition: PropTypes.func,
};

export default {
  CONSTANTS,
  GTM_KEY_GETTERS,
  buildGtag,
};
